import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Users, Mail, MailCheck, Search, RefreshCw,
  Send, RotateCcw, CheckCircle2, Clock, UserCheck,
} from 'lucide-react';
import adminService from '../../services/adminService';
import AdminNav from './AdminNav';
import { cn } from '../../lib/utils';

const STATUS_META = {
  new:       { label: 'New',       className: 'bg-gray-100 text-gray-700' },
  contacted: { label: 'Invited',   className: 'bg-cyan-100 text-cyan-700' },
  converted: { label: 'Converted', className: 'bg-emerald-100 text-emerald-700' },
};

const PORTFOLIO_LABELS = {
  '1-5':    '1–5 units',
  '6-20':   '6–20 units',
  '21+':    '21+ units',
  'unknown': '—',
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', meta.className)}>
      {meta.label}
    </span>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({}); // leadId → true
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsData, statsData] = await Promise.all([
        adminService.getLeads(),
        adminService.getLeadStats(),
      ]);
      setLeads(leadsData);
      setStats(statsData);
    } catch (err) {
      showToast('Failed to load leads', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (lead) => {
    setActionLoading(prev => ({ ...prev, [lead._id]: true }));
    try {
      await adminService.inviteLead(lead._id);
      showToast(`Invite sent to ${lead.email}`);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send invite', 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [lead._id]: false }));
    }
  };

  const handleResend = async (lead) => {
    setActionLoading(prev => ({ ...prev, [lead._id]: true }));
    try {
      await adminService.resendInvite(lead._id);
      showToast(`Invite resent to ${lead.email}`);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to resend invite', 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [lead._id]: false }));
    }
  };

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    return !q || l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q);
  });

  const invitedCount = leads.filter(l => l.status === 'contacted' || l.invitedAt).length;

  return (
    <div>
      <AdminNav />
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-top-2',
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
        )}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage waitlist sign-ups and send invites</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: Users,     label: 'Total leads',  value: stats?.total ?? '—' },
          { icon: Mail,      label: 'Invited',       value: invitedCount },
          { icon: MailCheck, label: 'Welcome sent',  value: stats?.welcomeEmailsSent ?? '—' },
        ].map(({ icon: Icon, label, value }) => (
          <Card key={label} className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-cyan-50 flex items-center justify-center">
                <Icon className="h-4 w-4 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Table card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">All Leads</CardTitle>
            <div className="relative w-60">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                placeholder="Search name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              {search ? 'No leads match your search' : 'No leads yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    {['Name', 'Email', 'Portfolio', 'Source', 'Status', 'Invited', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(lead => {
                    const busy = actionLoading[lead._id];
                    const isInvited = lead.status === 'contacted' || !!lead.invitedAt;
                    return (
                      <tr key={lead._id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{lead.name}</td>
                        <td className="px-4 py-3 text-gray-600">{lead.email}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{PORTFOLIO_LABELS[lead.portfolioSize] || lead.portfolioSize}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{lead.source || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {lead.invitedAt ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                              {new Date(lead.invitedAt).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Clock className="h-3 w-3" />
                              Not yet
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isInvited ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={busy}
                              onClick={() => handleResend(lead)}
                            >
                              {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                              Resend
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700"
                              disabled={busy}
                              onClick={() => handleInvite(lead)}
                            >
                              {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                              Invite
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default AdminLeads;
